/**
 * Per-column filter popover for the data grid (ADR-0010 G1).
 *
 * The filter for each column is CONFIGURED by the admin per-column (no blind
 * distinct): `text` → a free-text `contains`; `select` → a multiselect over the
 * admin-provided `options` (closed-domain fields: status, category…). The
 * emitted filter is applied SERVER-side by the `data_list`'s reactive query.
 *
 * The popover is `position: fixed` (computed from the trigger) AND portalled to
 * `document.body` so it escapes BOTH the table's `overflow-x-auto` clip and a
 * **pinned** (`position: sticky`) column's stacking/overflow context — which
 * would otherwise crop it. The portal is wrapped in a `.tray-light` div so it
 * keeps the portal shell's light tokens (a bare body portal → black popover).
 */
import { Check, Filter } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** An active column filter: a multiselect (`in`), a free-text (`contains`), or
 *  a numeric/date range (`range`, expanded to gte/lte on the server). */
export type ColumnFilterValue =
  | { op: "in"; value: string[] }
  | { op: "contains"; value: string }
  | { op: "range"; min?: string; max?: string }
  // `has`: a relation_many membership filter — rows whose link set includes
  // this related-row id (D20). The label is for display only; the id is sent.
  | { op: "has"; value: string };

const POPOVER_W = 224;

export function ColumnFilter({
  column,
  mode,
  options,
  relationOptions = [],
  value,
  onChange,
}: {
  column: string;
  /** Filter UX configured by the admin for this column. `daterange` is a
   *  `range` whose min/max are native date inputs (for date/datetime cols).
   *  `relation` is a single-select over `relationOptions` (relation_many `has`). */
  mode: "text" | "select" | "range" | "daterange" | "relation";
  /** Multiselect options (admin-provided), used when `mode === "select"`. */
  options: string[];
  /** Related-row choices `{id,label}` for a `relation` filter (auto-loaded by
   *  the data grid from the column's target table). */
  relationOptions?: { id: string; label: string }[];
  value: ColumnFilterValue | null;
  onChange: (v: ColumnFilterValue | null) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH: number } | null>(null);
  const [text, setText] = useState(value?.op === "contains" ? value.value : "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(value?.op === "in" ? value.value : []),
  );
  const [min, setMin] = useState(value?.op === "range" ? value.min ?? "" : "");
  const [max, setMax] = useState(value?.op === "range" ? value.max ?? "" : "");
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const active = value != null;
  const isRange = mode === "range" || mode === "daterange";
  const isDateRange = mode === "daterange";
  const asRelation = mode === "relation";
  // A `select` with no configured options degrades to text-contains.
  const asSelect = mode === "select" && options.length > 0;
  const relValue = value?.op === "has" ? value.value : null;
  const pickRelation = (id: string) => {
    onChange(relValue === id ? null : { op: "has", value: id });
    setOpen(false);
  };

  useEffect(() => {
    setText(value?.op === "contains" ? value.value : "");
    setSelected(new Set(value?.op === "in" ? value.value : []));
    setMin(value?.op === "range" ? value.min ?? "" : "");
    setMax(value?.op === "range" ? value.max ?? "" : "");
  }, [value]);

  // Outside-click closes. The popover is portalled to <body>, so it's NOT a DOM
  // child of wrapRef — check the popover ref too, else clicking inside it closes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !popoverRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const openPopover = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r && typeof window !== "undefined") {
      // Two native date inputs (each with a calendar icon) don't fit the
      // default width — widen for a date range so neither gets clipped.
      const wanted = isDateRange ? 312 : POPOVER_W;
      const width = Math.min(wanted, window.innerWidth - 16);
      // Anchor by the RIGHT edge of the trigger so a wider popover opens
      // leftward (the filter button sits near the right of the header).
      const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
      const top = r.bottom + 4;
      setPos({ top, left, width, maxH: window.innerHeight - top - 8 });
    }
    setOpen(true);
  };

  const toggle = (v: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  const apply = () => {
    if (isRange) {
      const lo = min.trim();
      const hi = max.trim();
      onChange(lo || hi ? { op: "range", min: lo || undefined, max: hi || undefined } : null);
    } else if (asSelect && selected.size > 0) onChange({ op: "in", value: [...selected] });
    else if (text.trim()) onChange({ op: "contains", value: text.trim() });
    else onChange(null);
    setOpen(false);
  };

  const clear = () => {
    setSelected(new Set());
    setText("");
    setMin("");
    setMax("");
    onChange(null);
    setOpen(false);
  };

  const q = text.trim().toLowerCase();
  const shown = options.filter((o) => !q || o.toLowerCase().includes(q));
  const relShown = relationOptions.filter((o) => !q || o.label.toLowerCase().includes(q));

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        aria-label={`Filter ${column}`}
        aria-pressed={active}
        onClick={() => (open ? setOpen(false) : openPopover())}
        className={`rounded p-0.5 transition ${
          active ? "text-accent" : "text-muted-foreground/50 hover:text-foreground"
        }`}
      >
        <Filter className="h-3 w-3" fill={active ? "currentColor" : "none"} />
      </button>
      {open && pos && createPortal(
        // `.tray-light` re-applies the portal's light tokens (lost when we leave
        // the shell subtree for <body>); the fixed popover then escapes ANY
        // pinned-column / overflow clip.
        <div className="tray-light">
        <div
          ref={popoverRef}
          role="dialog"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxH }}
          className="portal-pop z-[60] flex flex-col overflow-hidden rounded-[var(--portal-radius)] border border-border bg-elevated p-2 text-foreground shadow-xl"
        >
          {isRange ? (
            <div className="mb-2 flex items-center gap-1.5">
              <input
                autoFocus
                type={isDateRange ? "date" : "number"}
                value={min}
                placeholder={isDateRange ? "From" : "Min"}
                onChange={(e) => setMin(e.target.value)}
                className="w-full min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <span className="shrink-0 text-xs text-muted-foreground">–</span>
              <input
                type={isDateRange ? "date" : "number"}
                value={max}
                placeholder={isDateRange ? "To" : "Max"}
                onChange={(e) => setMax(e.target.value)}
                className="w-full min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>
          ) : (
            <input
              autoFocus
              value={text}
              placeholder={asSelect ? "Find…" : "Contains…"}
              onChange={(e) => setText(e.target.value)}
              className="mb-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          )}
          {asSelect && (
            <div className="min-h-0 flex-1 overflow-auto">
              {shown.length === 0 && <p className="px-1 py-1 text-xs text-muted-foreground">No values</p>}
              {shown.map((o) => {
                const on = selected.has(o);
                return (
                  <button
                    key={o}
                    type="button"
                    onClick={() => toggle(o)}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-accent/10"
                  >
                    <span
                      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                        on ? "border-accent bg-accent text-accent-foreground" : "border-border"
                      }`}
                    >
                      {on && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span className="truncate">{o}</span>
                  </button>
                );
              })}
            </div>
          )}
          {asRelation && (
            <div className="min-h-0 flex-1 overflow-auto">
              {relShown.length === 0 && <p className="px-1 py-1 text-xs text-muted-foreground">No values</p>}
              {relShown.map((o) => {
                const on = relValue === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => pickRelation(o.id)}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-accent/10"
                  >
                    <span
                      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
                        on ? "border-accent bg-accent text-accent-foreground" : "border-border"
                      }`}
                    >
                      {on && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
            <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">
              Clear
            </button>
            {!asRelation && (
              <button
                type="button"
                onClick={apply}
                className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground hover:bg-accent/90"
              >
                Apply
              </button>
            )}
          </div>
        </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
